/**
 * @file   src\Pages\AddNewHardware.tsx
 * @brief  Add New Hardware page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {React, Container, Breadcrumb, Button, Row, Col, Form, useOutletContext, useNavigate, useEffect, useState,  useParams } from '../utils/thirdpartyComponents';
import CWInput from '../components/CWInput';
import CWTextarea from '../components/CWTextarea';
import CWInputGroup from '../components/CWInputGroup';
import CWSelect from '../components/CWSelect';
import Fileimgicon from '../assets/images/photo-icon.svg';
import { pageURLs } from '../utils/pageUrls';
import { AppContextType } from '../interfaces/generalInterface';
import { addHardwareSchema } from '../validations/addHardwareSchema';
import * as Yup from 'yup'
import { ITEM_DESCRIPTION_LENGTH, ITEM_NAME_LENGTH, ITEM_PRICE_LENGTH } from '../utils/constants';
import { ManageHardwareApi, CommonApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import { MessageToaster } from '../utils/toastUtil';
import { validateImageTypes } from '../utils/helper';
import SmallLoader from '../components/SmallLoader';
import CloseIcon from '../assets/images/icons/close';
import { DeviceType } from '../utils/enums';

const AddNewHardware = (props: any) => {

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();
    const navigate = useNavigate()
    const param = useParams();
    const hardware_id = param.id ? parseInt(param.id) : 0;;
    const toaster = new MessageToaster();
    const [hardWareImageError, setHardWareImageError] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitDisable, setSubmitDisable] = useState(false);
    const [deviceTypeOptions] = useState([
        { label: "PAX", value: DeviceType.PAX },
        // { label: "LPR", value: DeviceType.LPR },
        { label: "Arduino Device", value: DeviceType.WASH_DEVICE },
    ]);



    const [formData, setFormData] = useState({
        hardwareName: '',
        description: '',
        price: 0,
        image: '',
        deviceType: DeviceType.WASH_DEVICE
    })

    // Initialise states for errors
    const [errors, setErrors] = useState({
        hardwareName: '',
        description: '',
        price: '',
        image: '',
        deviceType: ''
    });

    useEffect(() => {
        handleUpdate()
        if (props.edit)
            getHardwareDetails(hardware_id)
    }, [])

    // Update the data
    const handleUpdate = async () => {
        await updateData({ heading: props.edit ? "Edit item" : "Add New item", url: `/superadmin/${pageURLs.manage_hardware}`, showBackButton: true, showMainMenu: true, activeLink: "manage-hardware" });
    };

    // Navigate accordung to url
    const itemClick = async (url: string) => {
        navigate(url);
    }

    // Triggers on submit
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {

        event.preventDefault()
        try {
            // Validate form data
            await addHardwareSchema.validate(formData, { abortEarly: false });
            setLoading(true)
            //Clear errors
            setErrors({
                hardwareName: '',
                description: '',
                price: '',
                image: '',
                deviceType: ''
            });

            let response;
            if (props.edit) {
                const reqparam = {
                    ...formData,
                    hardwareId: hardware_id
                };
                response = await axios.post(ManageHardwareApi.UPDATE_HARDWARE, reqparam);

            }
            else {
                response = await axios.post(ManageHardwareApi.ADD_HARDWARE, formData);
            }
            setLoading(false)
            if (response?.data?.error == 0) {
                toaster.toastSuccess(response?.data?.message);
                navigate(`/superadmin/${pageURLs.manage_hardware}`)
            }
            else {
                toaster.toastError(response?.data?.message);

            }

        } catch (error) {
            const newErrors: any = {};

            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }
        }

    }

    //get hardware details by id
    const getHardwareDetails = async (id: number) => {
        setLoading(true)
        const response = await axios.get(ManageHardwareApi.HARDWARE_DETAILS + id);
        setLoading(false)
        if (response?.data?.error == 0) {
            const hardwareData = response?.data?.data
            setFormData({
                hardwareName: hardwareData.hardwareName,
                description: hardwareData.description,
                price: hardwareData.price,
                image: hardwareData.image,
                deviceType: Number(hardwareData.deviceType) || DeviceType.WASH_DEVICE
            })
        }
        else {
            toaster.toastError(response?.data?.message);
        }
    }

    // Triggers whenever input is entered
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = event.target

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        if (name == 'price') {
            setFormData({
                ...formData,
                [name]: parseFloat(value),
            });
        }
        else {
            setFormData({
                ...formData,
                [name]: value,
            });
        }


    }

    // Triggers whenever dropdown value is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption?.value || '';
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: Number(value),
        });
    }
    //Upload Image
    const uploadHardWareImage = async (e: any) => {
        setHardWareImageError('');
        if (e.target.files && e.target.files[0]) {
            const img = e.target.files[0];
            const isValid = validateImageTypes(img);
            if (isValid) {
                const folderName = "hardware/"
                const randomNumber = Math.floor(Math.random() * 1000000);
                const extension = img.name.substring(img.name.lastIndexOf("."));
                const newImageName = randomNumber + extension;
                setLoading(true)
                setSubmitDisable(true)
                const request_params = {
                    key: folderName + newImageName
                }

                const response = await axios.post(CommonApi.PRESIGNED_URL, request_params);
                if (response?.data?.error === 0) {
                    const uploadURL = response.data.data
                    if (uploadURL) {
                        try {
                            // PUT request: upload file to S3
                            const result = await fetch(uploadURL, {
                                method: "PUT",
                                body: img,
                            });
                            if (result.status === 200) {
                                setLoading(false)
                                setFormData({
                                    ...formData,
                                    ['image']: folderName + newImageName,
                                });
                            } else {
                                setLoading(false)
                            }
                        } catch (e) {
                            setLoading(false)
                            console.log(e)
                        }
                    } else {
                        setLoading(false)
                    }
                } else {
                    setLoading(false)
                    setHardWareImageError(response?.data?.message)
                }
                setSubmitDisable(false)
            } else {
                setHardWareImageError("Upload image files with .png or .jpeg extensions only.")
            }
        }
    }


    const removeCompanyLogo = async () => {
        setFormData({
            ...formData,
            ['image']: '',
        });
    }


    return (

        <div className='hardware-listing-container addnew-item-wrap'>
            <Container>
                <div className="nav-btn-title-sec d-flex justify-content-between align-items-center">
                    <Breadcrumb>
                        <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_hardware}`) }}>Hardware Listing</Breadcrumb.Item>
                        <Breadcrumb.Item onClick={() => { itemClick(`/superadmin/${pageURLs.manage_hardware}`) }}> Manage Hardware</Breadcrumb.Item>
                        <Breadcrumb.Item active>Add New Item</Breadcrumb.Item>
                    </Breadcrumb>
                </div>
                <h6 className='f-bold title'>Add Items</h6>
                <form onSubmit={(e) => handleSubmit(e)} >
                    <div className="hardware-list-wrap">
                        <div className="form-inner mx-auto">
                            <Row>
                                <Col md={6}>
                                    <CWInput
                                        id="hardwareName"
                                        name="hardwareName"
                                        type="text"
                                        label="Item Name"
                                        value={formData.hardwareName}
                                        isMandatory
                                        maxLength={ITEM_NAME_LENGTH}
                                        errorMessage={errors.hardwareName}
                                        onChange={handleInputChange}

                                    />
                                </Col>
                                <Col md={6}>
                                    <CWInputGroup
                                        id="price"
                                        name="price"
                                        type="number"
                                        label="Item Price"
                                        labelPrefix="$"
                                        value={formData.price}
                                        maxLength={ITEM_PRICE_LENGTH}
                                        errorMessage={errors.price}
                                        onChange={handleInputChange}
                                        isMandatory
                                    /></Col>
                            </Row>
                            
                            <Row>
                                <Col md={6}>
                                    <CWTextarea
                                        id="description"
                                        name="description"
                                        type="text"
                                        label="Item Description"
                                        value={formData.description}
                                        errorMessage={errors.description}
                                        maxLength={ITEM_DESCRIPTION_LENGTH}
                                        onChange={handleInputChange}
                                        isMandatory
                                    />
                                </Col>
                                <Col md={6}>
                                    <div className="file-uploader-wrap">
                                        <div className="file-upload d-flex align-items-center justify-content-center">
                                            <div className="upload-info text-center">
                                                {formData.image && <div className="preview-logo">
                                                    <div className="common-cover d-flex align-items-center justify-content-center">
                                                        <Button variant="light" className="light-btn close" onClick={removeCompanyLogo}><CloseIcon /></Button>
                                                        <img src={`${process.env.REACT_APP_S3_URL_PUBLIC_PATH || ""}${formData.image}`} alt="Car Wash App" width={"139px"} height={"139px"} />
                                                    </div>
                                                </div>}
                                                {!formData.image && <> <img src={Fileimgicon} alt="" />
                                                    <p className='f-semi'>Upload Image</p></>}
                                            </div>
                                            <Form.Control type="file" multiple className='file-input' onChange={uploadHardWareImage} />

                                        </div>
                                        <span className="upload-note">Note: Accepted image formats are .jpeg and .png</span>
                                    </div>
                                    {hardWareImageError && (
                                        <span className="error-msg">{hardWareImageError}</span>
                                    )}
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <CWSelect
                                        id="deviceType"
                                        name="deviceType"
                                        options={deviceTypeOptions}
                                        label="Device Type"
                                        dropdownGap={0}
                                        dropdownPosition="auto"
                                        placeholder="Select Device Type"
                                        multi={false}
                                        searchable={false}
                                        isMandatory
                                        disabled={props.edit}
                                        values={deviceTypeOptions.filter(option => option.value === formData.deviceType)}
                                        errorMessage={errors.deviceType}
                                        onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'deviceType')}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </div>
                    <div className="btn-sec d-flex justify-content-end">
                        <Button title="Cancel" variant="primary" className="secondary-btn" onClick={() => navigate(`/superadmin/${pageURLs.manage_hardware}`)}>Cancel</Button>
                        <Button title="Save" variant="primary" className="primary-btn" type="submit" disabled={submitDisable}>Save</Button>
                    </div>
                </form>
                {loading && <SmallLoader />}
            </Container >
        </div >
    )
}

export default AddNewHardware;
